var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '@services/authService';
const AuthContext = createContext(undefined);
// Reads the JWT's exp claim (not verified - the API does that) to skip restoring a dead session
const isTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload.exp !== 'number' || payload.exp * 1000 <= Date.now();
    }
    catch (_a) {
        return true;
    }
};
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const storeSession = (response) => {
        setToken(response.token);
        setUser(response.user);
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
    };
    useEffect(() => {
        // Load token from localStorage on mount
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        // Extends the session to the full expiry (90 days) each time the user comes back
        let lastRefresh = 0;
        const refreshSession = () => {
            lastRefresh = Date.now();
            authService.refresh()
                .then(storeSession)
                .catch(() => {
                // Offline or API unreachable - keep the current session (a 401 is handled by apiClient)
            });
        };
        if (storedToken && storedUser && !isTokenExpired(storedToken)) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            refreshSession();
        }
        else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        }
        setLoading(false);
        // Switching back to a tab left open (common on phones) doesn't reload the app, so refresh then too - at most hourly
        const onVisible = () => {
            if (document.visibilityState === 'visible' && localStorage.getItem('token') && Date.now() - lastRefresh > 60 * 60 * 1000) {
                refreshSession();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []);
    const login = (credentials) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield authService.login(credentials);
        storeSession(response);
    });
    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    };
    const hasRole = (role) => {
        var _a;
        return ((_a = user === null || user === void 0 ? void 0 : user.roles) === null || _a === void 0 ? void 0 : _a.includes(role)) || false;
    };
    const value = {
        user,
        token,
        login,
        loading,
        logout,
        isAuthenticated: !!token,
        isAdmin: hasRole('Admin'),
        isOwner: hasRole('Owner'),
        hasRole,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
